import type { DeckId, Look, Rules, SceneId, Tempo, Variant } from "@app/protocol";
import { useT } from "../i18n/context";
import { Choice, Panel } from "./Choices";
import { GAMES, GameCard, SettingsHead } from "./GameTiles";
import { HouseRules } from "./HouseRules";
import { LookPicker } from "./LookPicker";
import { canOpen } from "./lookPick";
import type { LooksState } from "./useLooks";

const TEMPOS: readonly Tempo[] = ["relaxed", "normal", "fast"];

/** What the table will be; `goal` null plays by hand count. */
export interface TableChoice {
  variant: Variant;
  goal: number | null;
  tempo: Tempo;
  listed: boolean;
  rules: Rules;
  scene: SceneId;
  deck: DeckId;
}

/** Set up a table (design 2): the game, its target, the tempo, who may join, house rules, scene and deck. */
export function CreateTable({
  choice,
  goals,
  length,
  lengths,
  onLength,
  onChange,
  onGame,
  onOpen,
  looks,
}: {
  choice: TableChoice;
  goals: readonly number[];
  length: number;
  /** The game's own match lengths (King: 20 or 12 hands); null when it plays to a goal or the table's length. */
  lengths: readonly number[] | null;
  onLength: (hands: number) => void;
  onChange: (patch: Partial<TableChoice>) => void;
  onGame: (variant: Variant) => void;
  onOpen: () => void;
  looks: LooksState;
}) {
  const { t } = useT();
  const pickLook = (look: Look) =>
    onChange(look.kind === "scene" ? { scene: look.id } : { deck: look.id });
  return (
    <section aria-labelledby="create-title" className="flex flex-col gap-[22px]">
      <div className="flex flex-col gap-1">
        <h2 id="create-title" className="text-legible text-[26px] font-extrabold">
          {t("home.title")}
        </h2>
        <p className="text-[14px] text-ink/80">{t("home.subtitle")}</p>
      </div>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(max(160px,22%),1fr))] gap-3">
        {GAMES.map((game) => (
          <GameCard
            key={game.variant}
            game={game}
            selected={game.variant === choice.variant}
            onClick={() => onGame(game.variant)}
          />
        ))}
      </div>
      <div className="flex flex-col rounded-[18px] border-[1.5px] border-edge bg-panel-strong shadow-pill backdrop-blur-[10px]">
        <SettingsHead variant={choice.variant} />
        <Panel label={t("home.goal")}>
          {goals.map((goal) => (
            <Choice
              key={goal}
              label={t("home.points", { goal })}
              selected={choice.goal === goal}
              onClick={() => onChange({ goal })}
            />
          ))}
          {(lengths ?? [length]).map((hands) => (
            <Choice
              key={hands}
              label={t("home.handsCount", { length: hands })}
              selected={choice.goal === null && hands === length}
              onClick={() => onLength(hands)}
            />
          ))}
        </Panel>
        <Panel label={t("home.tempo")}>
          {TEMPOS.map((tempo) => (
            <Choice
              key={tempo}
              label={t(`tempo.${tempo}`)}
              selected={choice.tempo === tempo}
              onClick={() => onChange({ tempo })}
            />
          ))}
        </Panel>
        <Panel label={t("home.who")}>
          <Choice
            label={t("home.who.anyone")}
            selected={choice.listed}
            onClick={() => onChange({ listed: true })}
          />
          <Choice
            label={t("home.who.invite")}
            selected={!choice.listed}
            onClick={() => onChange({ listed: false })}
          />
        </Panel>
        <HouseRules
          variant={choice.variant}
          rules={choice.rules}
          onChange={(rules) => onChange({ rules })}
        />
      </div>
      <LookPicker kind="scene" selected={choice.scene} state={looks} onChange={pickLook} />
      <LookPicker kind="deck" selected={choice.deck} state={looks} onChange={pickLook} />
      <div className="flex justify-end">
        <button
          type="button"
          className="btn-gold h-[52px] rounded-pill px-7 text-[15px] font-extrabold disabled:cursor-wait disabled:opacity-60"
          disabled={!canOpen(looks.pending)}
          onClick={onOpen}
        >
          {t("home.open")}
        </button>
      </div>
    </section>
  );
}
